/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from "react";
import { fixEquipmentFields } from "../fields/fixEquipmentFields";
import SimpleBody from "../../../../components/forms/SimpleBody";
import useNotification from "../../../../hooks/useNotification";
import useBaseService from "../../../../hooks/services/useBaseService";
import { useNavigate } from "react-router-dom";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useForm from "../../../../hooks/useForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { transformFiltersForUrl } from "../../../../utils/transformFiltersForUrl";
import { entities } from "../../../../constants/entities";
import useLoader from "../../../../hooks/useLoader";
import useRemoteSelectOptions from "../../../../hooks/useRemoteSelectOptions";

/**
 * * Lógica para formulário de retombar equipamentos
 */
export function useRelabelEquipment({
  formData,
  setFormData,
  equipmentOptions,
}) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const queryClient = useQueryClient();

  const {
    get: baseServiceGet,
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const [_a, _b, relabelProductSection, relabelEquipmentSection] =
    fixEquipmentFields;

  const {
    formData: relabelProductFormData,
    handleChange: relabelProductChange,
    setFormData: setRelabelProductFormData,
  } = useForm(setDefaultFieldValues([relabelProductSection]), [
    relabelProductSection,
  ]);

  const {
    formData: relabelEquipmentFormData,
    handleChange: relabelEquipmentChange,
    setFormData: setRelabelEquipmentFormData,
  } = useForm(setDefaultFieldValues([relabelEquipmentSection]), [
    relabelEquipmentSection,
  ]);

  // Encontra as informaçoes do equipamento selecionado no form de "Qual situação do equipamento?"
  const mathEquipment = useCallback(() => {
    const equipmentId = formData.equipment.equipment_id?.value;

    if (equipmentId) {
      const equipment = equipmentOptions.rawData.find(
        (item) => item.id === equipmentId,
      );

      if (equipment) {
        // Seta as informações de produto
        setRelabelProductFormData((prev) => ({
          ...prev,
          productItem: {
            ...prev.productItem,
            name: equipment.product.name,
            category: equipment.category.name,
            condition: equipment.condition.name,
            address: `${equipment.creation_transaction_item.address.alias} - ${equipment.creation_transaction_item.address.street}, ${equipment.creation_transaction_item.address.number} - ${equipment.creation_transaction_item.address.district}, ${equipment.creation_transaction_item.address.city} - ${equipment.creation_transaction_item.address.state}`,
          },
        }));

        setRelabelEquipmentFormData((prev) => ({
          ...prev,
          equipment: {
            ...prev.equipment,
            serial_number: equipment.serial_number,
          },
        }));
      }

      return equipment;
    }
  }, [
    formData.equipment.equipment_id?.value, // necessário para pegar o valor atualizado
    equipmentOptions.rawData,
    setRelabelProductFormData,
  ]);

  const prevEquipmentRef = useRef();

  useEffect(() => {
    const currentId = formData.equipment.equipment_id?.value;

    if (prevEquipmentRef.current !== currentId) {
      mathEquipment();
    }

    prevEquipmentRef.current = currentId;
  }, [formData.equipment.equipment_id?.value, mathEquipment]);

  const createRelabel = useMutation({
    mutationFn: async (payload) => {
      const response = await baseServicePost(
        entities.equipments.relabel.create(
          formData.equipment.equipment_id?.value,
        ),
        payload,
      );
      return response;
    },
    onMutate: () => {
      showLoader();
    },
    onSuccess: (response) => {
      hideLoader();
      if (response?.status === 201) {
        queryClient.invalidateQueries(["equipments"]);
        showNotification("Equipamento retombado com sucesso!");
        navigate("/aluguel/equipamentos/");
      }
    },
    onError: (error) => {
      hideLoader();
      console.error(error);

      if (error.response?.data?.errors) {
        setFormErrors((prev) => ({
          ...prev,
          ...error.response.data.errors,
        }));
      }
    },
  });

  const renderRelabelPrductFormBody = useCallback(() => {
    return (
      <SimpleBody
        fields={relabelProductSection.fields}
        formErrors={normalizeFormErrors(formErrors)}
        formData={relabelProductFormData}
        handleFieldChange={relabelProductChange}
        getOptions={() => {}}
        getSelectedValue={() => {}}
        getSelectMeta={() => {}}
      />
    );
  }, [
    relabelProductFormData,
    formErrors,
    relabelProductChange,
    relabelProductSection.fields,
  ]);

  const renderRelabelEquipmentFormBody = useCallback(() => {
    return (
      <SimpleBody
        fields={relabelEquipmentSection.fields}
        formErrors={normalizeFormErrors(formErrors)}
        formData={relabelEquipmentFormData}
        handleFieldChange={relabelEquipmentChange}
        getOptions={() => {}}
        getSelectedValue={() => {}}
      />
    );
  }, [
    relabelEquipmentFormData,
    formErrors,
    relabelEquipmentChange,
    relabelEquipmentSection.fields,
  ]);

  return {
    // * Produto
    relabelProductFormData,
    relabelProductChange,
    setRelabelProductFormData,

    // * Equipamento
    relabelEquipmentFormData,
    relabelEquipmentChange,
    setRelabelEquipmentFormData,

    // * Mutations
    createRelabel,

    // * Form
    renderRelabelPrductFormBody,
    renderRelabelEquipmentFormBody,
  };
}
